/**
 * Ask Why chat state: a module-level store so any surface (session page, dock,
 * cluster page, nav entry) can open the drawer with context. The drawer itself
 * is mounted by the pages that host it (SessionPage, AI pages) and by the
 * standalone /ai/ask route.
 */
import { useSyncExternalStore } from "react";
import type { ArtifactMeta, ChatEvent, Citation, Conversation, ConversationSummary } from "@backstory/protocol";
import { aiApi } from "../api";

export interface ChatContext {
  sessionId?: string;
  clusterId?: string;
  fromMs?: number;
  toMs?: number;
  /** Human label for the context strip. */
  label?: string;
}

export interface ChatCitation extends Citation {
  sessionId?: string;
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  tools: Array<{ name: string; summary?: string; done: boolean }>;
  citations: ChatCitation[];
  meta?: ArtifactMeta;
  error?: string;
  at: string;
}

export interface AskWhyState {
  open: boolean;
  context: ChatContext;
  conversationId?: string;
  messages: ChatMessage[];
  streaming: boolean;
  /** Set while the stored thread for this context is being fetched. */
  restoring: boolean;
  /** Cursor for the next page of older messages; absent at the start of a thread. */
  olderCursor?: string;
  loadingOlder: boolean;
  /** Earlier chats for this context, newest activity first. */
  history: ConversationSummary[];
  historyCursor?: string;
  historyLoading: boolean;
  historyFor?: string;
  /**
   * Context whose stored thread has already been looked for, so reopening the
   * drawer does not re-fetch and "New" is not immediately undone. Cleared when
   * the context changes, which is what lets a revisited session resume again.
   */
  restoredFor?: string;
  error?: string;
}

let state: AskWhyState = { open: false, context: {}, messages: [], streaming: false, restoring: false, loadingOlder: false, history: [], historyLoading: false };
/** Bumped when the thread on screen is discarded, so a restore still in flight cannot put it back. */
let epoch = 0;
const listeners = new Set<() => void>();
let controller: AbortController | null = null;

function set(patch: Partial<AskWhyState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}
function updateLast(fn: (m: ChatMessage) => ChatMessage) {
  const msgs = state.messages.slice();
  const last = msgs[msgs.length - 1];
  if (!last) return;
  msgs[msgs.length - 1] = fn(last);
  set({ messages: msgs });
}

let seq = 0;
const newId = () => `m_${Date.now().toString(36)}_${++seq}`;

/** Identity of a context, used to decide whether a thread still belongs on screen. */
const contextKey = (c: ChatContext) => JSON.stringify({ ...c, label: undefined });

type StoredMessage = Conversation["messages"][number];
const fromStored = (m: StoredMessage): ChatMessage => ({
  id: m.id,
  role: m.role,
  text: m.text,
  tools: [],
  citations: (m.citations ?? []) as ChatCitation[],
  at: m.at,
});

export const askWhy = {
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  get: () => state,

  open(context: ChatContext = {}) {
    // A new context resets the conversation so answers stay grounded in it.
    const same = contextKey(state.context) === contextKey(context);
    if (!same) epoch += 1;
    set({ open: true, context, ...(same ? {} : { conversationId: undefined, messages: [], olderCursor: undefined, restoredFor: undefined, history: [], historyCursor: undefined, historyFor: undefined, error: undefined }) });
  },
  close() {
    set({ open: false });
  },
  toggle(context?: ChatContext) {
    if (state.open) askWhy.close();
    else askWhy.open(context ?? state.context);
  },
  setContext(context: ChatContext) {
    set({ context });
  },
  stop() {
    controller?.abort();
    controller = null;
    set({ streaming: false });
    updateLast((m) => (m.role === "assistant" && !m.meta ? { ...m, text: m.text || "(stopped)" } : m));
  },
  /** Empty the store, including the memory of what has been restored. */
  reset() {
    epoch += 1;
    controller?.abort();
    controller = null;
    set({ conversationId: undefined, messages: [], olderCursor: undefined, restoredFor: undefined, streaming: false, restoring: false, loadingOlder: false, history: [], historyCursor: undefined, historyFor: undefined, historyLoading: false, error: undefined });
  },

  /** A blank thread. Earlier chats stay in the history. */
  newConversation() {
    epoch += 1;
    controller?.abort();
    controller = null;
    set({ conversationId: undefined, messages: [], olderCursor: undefined, restoredFor: contextKey(state.context), streaming: false, restoring: false, loadingOlder: false, error: undefined });
  },

  /** Load a stored conversation (its newest page). */
  async load(conversationId: string) {
    const c = await aiApi.conversation(conversationId);
    set({ conversationId: c.id, messages: c.messages.map(fromStored), olderCursor: c.nextCursor });
  },

  /**
   * Bring back the thread this user last talked in for the current context,
   * including a project-wide chat, and load the history beside it.
   */
  async restore() {
    const key = contextKey(state.context);
    const ticket = epoch;
    if (state.historyFor !== key) void askWhy.refreshHistory();
    if (state.restoredFor === key || state.messages.length || state.streaming) return;
    const { sessionId, clusterId } = state.context;
    set({ restoredFor: key, restoring: true });
    try {
      const { conversation } = await aiApi.latestConversation({ sessionId, clusterId });
      // Anything that arrived while we waited — a different context, a new
      // chat, or a question the user already asked — outranks the stored thread.
      if (ticket !== epoch || !conversation || contextKey(state.context) !== key || state.messages.length || state.streaming) return;
      set({ conversationId: conversation.id, messages: conversation.messages.map(fromStored), olderCursor: conversation.nextCursor });
    } catch {
      // A thread that cannot be read is not worth an error in the user's face;
      // they can still ask their question.
    } finally {
      if (ticket === epoch) set({ restoring: false });
    }
  },

  /** Reload the history list for the context on screen. */
  async refreshHistory() {
    const key = contextKey(state.context);
    const ticket = epoch;
    const { sessionId, clusterId } = state.context;
    set({ historyLoading: true, historyFor: key });
    try {
      const page = await aiApi.conversationHistory({ sessionId, clusterId });
      if (ticket !== epoch || contextKey(state.context) !== key) return;
      set({ history: page.items, historyCursor: page.nextCursor });
    } catch {
      // The list failing leaves the conversation usable.
    } finally {
      if (ticket === epoch && state.historyFor === key) set({ historyLoading: false });
    }
  },

  /** The next page of older chats. */
  async loadMoreHistory() {
    const { historyCursor } = state;
    if (!historyCursor || state.historyLoading) return;
    const key = contextKey(state.context);
    const ticket = epoch;
    const { sessionId, clusterId } = state.context;
    set({ historyLoading: true });
    try {
      const page = await aiApi.conversationHistory({ sessionId, clusterId, cursor: historyCursor });
      if (ticket !== epoch || contextKey(state.context) !== key) return;
      const known = new Set(state.history.map((h) => h.id));
      set({ history: [...state.history, ...page.items.filter((h) => !known.has(h.id))], historyCursor: page.nextCursor });
    } catch {
      /* the rows already on screen stay */
    } finally {
      if (ticket === epoch) set({ historyLoading: false });
    }
  },

  /** Open an earlier chat and keep talking in it. */
  async openConversation(id: string) {
    if (state.conversationId === id && state.messages.length) return;
    const ticket = epoch;
    controller?.abort();
    controller = null;
    set({ streaming: false, restoring: true, error: undefined });
    try {
      const c = await aiApi.conversation(id);
      if (ticket !== epoch) return;
      set({ conversationId: c.id, messages: c.messages.map(fromStored), olderCursor: c.nextCursor, restoredFor: contextKey(state.context) });
    } catch (e) {
      if (ticket === epoch) set({ error: (e as Error).message });
    } finally {
      if (ticket === epoch) set({ restoring: false });
    }
  },

  /** Remove one chat. The one on screen becomes a blank thread. */
  async removeConversation(id: string) {
    await aiApi.deleteConversation(id);
    const history = state.history.filter((h) => h.id !== id);
    if (state.conversationId === id) {
      epoch += 1;
      controller?.abort();
      controller = null;
      set({ conversationId: undefined, messages: [], olderCursor: undefined, restoredFor: contextKey(state.context), streaming: false, restoring: false, history });
      return;
    }
    set({ history });
  },

  /** Page backwards through a long thread as the reader scrolls up. */
  async loadOlder() {
    const { conversationId, olderCursor } = state;
    if (!conversationId || !olderCursor || state.loadingOlder) return;
    set({ loadingOlder: true });
    try {
      const c = await aiApi.conversation(conversationId, olderCursor);
      if (state.conversationId !== conversationId) return;
      const known = new Set(state.messages.map((m) => m.id));
      const older = c.messages.filter((m) => !known.has(m.id)).map(fromStored);
      set({ messages: [...older, ...state.messages], olderCursor: c.nextCursor });
    } catch (e) {
      set({ error: (e as Error).message });
    } finally {
      set({ loadingOlder: false });
    }
  },

  /** Send a message and stream the reply. Resolves when the stream ends. */
  async send(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed || state.streaming) return;
    set({ restoredFor: contextKey(state.context) }); // asking beats restoring, if both race
    controller?.abort();
    controller = new AbortController();
    const user: ChatMessage = { id: newId(), role: "user", text: trimmed, tools: [], citations: [], at: new Date().toISOString() };
    const assistant: ChatMessage = { id: newId(), role: "assistant", text: "", tools: [], citations: [], at: new Date().toISOString() };
    set({ messages: [...state.messages, user, assistant], streaming: true, error: undefined });
    const { sessionId, clusterId, fromMs, toMs } = state.context;
    try {
      for await (const ev of aiApi.chat({ conversationId: state.conversationId, message: trimmed, context: { sessionId, clusterId, fromMs, toMs } }, controller.signal)) {
        applyEvent(ev);
        if (ev.type === "done" || ev.type === "error") break;
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError") {
        set({ error: (e as Error).message });
        updateLast((m) => ({ ...m, error: (e as Error).message }));
      }
    } finally {
      controller = null;
      set({ streaming: false });
      void askWhy.refreshHistory();
    }
  },
};

function applyEvent(ev: ChatEvent) {
  switch (ev.type) {
    case "token":
      updateLast((m) => ({ ...m, text: m.text + ev.text }));
      break;
    case "tool_call":
      updateLast((m) => ({ ...m, tools: [...m.tools, { name: ev.name, done: false }] }));
      break;
    case "tool_result":
      updateLast((m) => {
        const tools = m.tools.slice();
        const i = tools.map((t) => t.name).lastIndexOf(ev.name);
        if (i >= 0) tools[i] = { ...tools[i]!, summary: ev.summary, done: true };
        else tools.push({ name: ev.name, summary: ev.summary, done: true });
        return { ...m, tools };
      });
      break;
    case "citation":
      updateLast((m) => ({ ...m, citations: [...m.citations, ev.citation] }));
      break;
    // The server assigns the thread id on the first turn and repeats it on every
    // terminal event. Keeping it is what makes a follow-up a follow-up: without
    // it each message would open a thread of its own and the model would answer
    // with no memory of the last one.
    case "done":
      updateLast((m) => ({ ...m, id: ev.messageId || m.id, meta: ev.meta }));
      if (ev.conversationId) set({ conversationId: ev.conversationId });
      break;
    case "error":
      set({ error: ev.message });
      updateLast((m) => ({ ...m, error: ev.message }));
      if (ev.conversationId) set({ conversationId: ev.conversationId });
      break;
  }
}

export function useAskWhy(): AskWhyState {
  return useSyncExternalStore(askWhy.subscribe, askWhy.get, askWhy.get);
}

/**
 * What the assistant is doing, in the words a user would use. Internal names
 * (transcript, cluster, DOM, trace) stay out of the product; a chip says what
 * was looked at, not which tool fetched it.
 */
const TOOL_LABELS: Record<string, string> = {
  search_sessions: "searching sessions",
  get_session_transcript: "reading what happened in the session",
  get_transcript: "reading what happened in the session",
  get_session_summary: "reading the session summary",
  get_summary: "reading the session summary",
  get_repro_steps: "reading the steps to reproduce",
  get_dom_snapshot_at: "looking at the page",
  get_dom_at: "looking at the page",
  get_request_details: "checking network requests",
  get_network: "checking network requests",
  get_trace: "following the request through the backend",
  get_issue_cluster: "looking up the issue",
  get_cluster: "looking up the issue",
  get_error_context: "reading the error",
  get_code: "reading the code",
  list_recent_issues: "listing recent issues",
};

export function toolLabel(name: string, done: boolean): string {
  const base = TOOL_LABELS[name] ?? name.replace(/_/g, " ");
  return done ? base : `${base}…`;
}

/**
 * The same step repeated (one per session read, say) folds into one chip with
 * a count: "reading what happened in 5 sessions" rather than five identical
 * chips. A group is running while any of its members is.
 */
export function groupTools(tools: Array<{ name: string; done: boolean; summary?: string }>): Array<{ label: string; done: boolean; count: number; summary?: string }> {
  const out: Array<{ label: string; done: boolean; count: number; summary?: string; key: string }> = [];
  for (const t of tools) {
    const key = TOOL_LABELS[t.name] ?? t.name;
    const prev = out.find((g) => g.key === key);
    if (prev) {
      prev.count += 1;
      prev.done = prev.done && t.done;
      if (t.summary) prev.summary = prev.summary ? `${prev.summary}\n${t.summary}` : t.summary;
    } else {
      out.push({ key, label: toolLabel(t.name, true), done: t.done, count: 1, summary: t.summary });
    }
  }
  return out.map(({ key: _k, ...g }) => {
    let label = g.label;
    if (g.count > 1) {
      // "the session" → "N sessions", "the issue" → "N issues"; anything else takes a suffix.
      if (label === "reading the session summary") label = `reading ${g.count} session summaries`;
      else if (/\bthe (session|issue|page|error)\b/.test(label)) label = label.replace(/\bthe (session|issue|page|error)\b/, `${g.count} $1s`);
      else label = `${label} ×${g.count}`;
    }
    return { ...g, label: g.done ? label : `${label}…` };
  });
}
